import { useMutation } from '@tanstack/react-query'
import intersection from 'lodash/intersection'

import { useTheme } from '@mui/material/styles'

import useQuery from '@shared/hooks/src/useQuery'
import { Lookup, useLookup } from '@shared/providers/src/DropdownOptionsProvider'
import { queryClient } from '@shared/providers/src/QueryClientProvider'
import API from '@shared/services/src/API'
import { handleError, QK, TreatmentType } from '@shared/utils'

import { FilterType } from '@components/TableFilters'

export default function useCBOsCoverage(services, options = {}) {
  const theme = useTheme()

  return useQuery({
    queryKey: QK.cbos.coverage,
    queryFn: () => API.cbos.coverage(),
    select: (data) => mapCBOsData(data, services, theme),
    ...options,
  })
}

export function usePriorityToggle(state) {
  return useMutation({
    mutationFn: (cbo) => {
      if (cbo.prioritized) {
        return API.cbos.id(cbo.id).priority.remove({ state })
      }
      return API.cbos.id(cbo.id).priority.add({ state })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QK.cbos.coverage }),
    onError: (error) => handleError(error, { showResponse: true }),
  })
}

const mapCBOsData = (data = [], services = [], theme) => {
  const priorities = data.reduce((acc, cbo) => {
    cbo.statesWithPriority?.forEach((state) => {
      acc[state] = cbo.id
    })
    return acc
  }, {})

  const cbos = data
    .filter((cbo) => cbo.acceptingNewPatients && cbo.coveredCounties?.length)
    .map(({ cbosProviderTypes = [], ...cbo }) => ({
      ...cbo,
      services: cbosProviderTypes.map(({ name }) => name),
      prepModalities: getPrEPModalities(cbosProviderTypes),
    }))
    .filter((cbo) => services.length === 0 || intersection(cbo.services, services).length)

  return cbos.reduce((acc, cbo) => {
    cbo.coveredCounties.forEach(({ state, counties }) => {
      const isFullyCovered = !counties || counties.length === 0
      let stateData = acc[state] || { fill: theme.palette.warning.main, cbos: [] }
      if (isFullyCovered) stateData.fill = theme.palette.success.main

      const cboData = {
        id: cbo.id,
        name: cbo.name,
        logo: cbo.logo,
        counties,
        services: cbo.services,
        prepModalities: cbo.prepModalities,
        isFullyCovered,
        prioritized: priorities[state] === cbo.id,
      }

      if (cboData.prioritized) {
        stateData.cbos.unshift(cboData)
      } else {
        stateData.cbos.push(cboData)
      }

      acc[state] = stateData
    })

    return acc
  }, {})
}

const getPrEPModalities = (cbosProviderTypes = []) => {
  const prep = cbosProviderTypes.find(({ name }) => name === TreatmentType.OralPrEP)
  if (!prep) return []

  return [
    { label: 'Scheduling', show: prep.scheduling },
    { label: 'Async Messaging', show: prep.asyncMessaging },
    { label: 'On Demand', show: prep.onDemand },
  ]
    .filter(({ show }) => show)
    .map(({ label }) => label)
}

export const useFiltersSchema = () => {
  const providerTypes = useLookup(Lookup.ProviderTypes)

  return [
    {
      key: 'services',
      type: FilterType.MULTI_SELECT,
      options: Object.keys(providerTypes).map((value) => ({ label: value, value })),
    },
  ]
}
